import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import {
    Button,
    Icon,
    ModalDialog,
    ModalDialogBody,
    ModalDialogButtons,
    ModalDialogContent,
    ModalDialogFooter,
    Spinner,
    SwanIconKey,
    responsive,
    useScreenClass,
} from '@vp/swan';
import { tokensRaw } from '@vp/swan';
import styled from 'styled-components';
import { __ } from '@99designs/i18n';
import { useBriefContext } from '../../BriefContext';
import { FieldGroupData } from '../FieldGroup';
import { useEditModal } from './EditModalContext';

const ResponsiveModalDialogContent = responsive(ModalDialogContent);

const ModalContentWrapper = styled.div`
    fieldset {
        margin: ${tokensRaw.SwanSemSpace5} 0 0 0;
    }
`;

export function EditFormGroupModal({
    showEditButton,
    editButtonIcon,
    groupHasErrors,
    group,
    children,
}: {
    showEditButton: boolean;
    groupHasErrors: boolean;
    group: FieldGroupData;
    editButtonIcon: SwanIconKey;
    children: React.ReactNode;
}) {
    const screenClass = useScreenClass();
    const { isOpen, isSaving, open, cancel, close } = useEditModal();
    const { trigger } = useFormContext();
    const { isUpdatingProduct: isUpdating } = useBriefContext();
    const dialogRef = React.useRef<HTMLDialogElement>(null);

    // Prevents the modal from closing when clicking outside the modal
    useEffect(() => {
        if (dialogRef.current) {
            dialogRef.current.addEventListener(
                'click',
                (e) => {
                    if (e.target === e.currentTarget) {
                        e.stopImmediatePropagation();
                    }
                },
                true
            );
        }
    }, []);

    // Trigger validation when the modal is closed to ensure cancelling clears any existing validation errors.
    // Also triggers on initial mount since we want all fields to be filled out
    useEffect(() => {
        if (!isOpen) void trigger();
    }, [trigger, isOpen]);

    return (
        <>
            <ModalDialog ref={dialogRef} isOpen={isOpen} onRequestDismiss={() => null}>
                <ResponsiveModalDialogContent
                    aria-labelledby="modal dialog general"
                    // Scroll only beyond the viewport.
                    lg={{ style: { minWidth: '800px', maxHeight: 'calc(100vh - 2rem)' } }}
                    sm={{ style: { minWidth: '600px', maxHeight: 'calc(100vh - 2rem)' } }}
                    paddingBottom={0}
                >
                    <ModalDialogBody>
                        <ModalContentWrapper>{children}</ModalContentWrapper>
                    </ModalDialogBody>
                    <ModalDialogFooter
                        style={{
                            position: 'sticky',
                            height: '94px',
                            bottom: 0,
                            backgroundColor: 'white',
                            zIndex: 1000,
                        }}
                    >
                        <ModalDialogButtons>
                            <Button skin="secondary" disabled={isUpdating} onClick={cancel}>
                                {isSaving && <Spinner accessibleText={__('Loading...')} />}{' '}
                                {__('Cancel')}
                            </Button>
                            <Button
                                disabled={groupHasErrors || isUpdating}
                                skin="primary"
                                onClick={close}
                            >
                                {__('Save')}
                            </Button>
                        </ModalDialogButtons>
                    </ModalDialogFooter>
                </ResponsiveModalDialogContent>
            </ModalDialog>
            {showEditButton && (
                <Button
                    data-testid={`edit-button-${group.id}`}
                    style={
                        screenClass === 'xs'
                            ? {
                                  marginLeft: 'auto',
                                  position: 'absolute',
                                  top: '4px',
                                  right: '4px',
                                  width: '44px',
                                  height: '44px',
                              }
                            : {
                                  position: 'absolute',
                                  top: '24px',
                                  right: '24px',
                                  width: '44px',
                                  height: '44px',
                              }
                    }
                    skin={'tertiary'}
                    onClick={open}
                >
                    <Icon iconType={editButtonIcon} />
                </Button>
            )}
        </>
    );
}
